import { Button, Tag, Typography, theme } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { UploadedFile } from '@/types/dashboard'
import { Loader2, Sparkles } from 'lucide-react'

const { Text } = Typography

interface FileListProps {
  files: UploadedFile[]
  onRemove: (id: number) => void
  isProcessing?: boolean
}

export default function FileList({ files, onRemove, isProcessing }: FileListProps) {
  const { token } = theme.useToken()

  if (files.length === 0) return null

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
          File upload: {files.length} file{files.length > 1 ? 's' : ''}
        </Text>
        {isProcessing && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
            <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />
            <span className="text-xs text-cyan-400 font-medium">
              Creating session...
            </span>
          </div>
        )}
      </div>

      {/* File List */}
      <div className="space-y-2">
        {files.map((file) => (
          <div
            key={file.id}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
              isProcessing
                ? 'bg-slate-800/30 border-cyan-500/20'
                : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
            }`}
          >
            {/* File Icon/Thumbnail */}
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center border relative overflow-hidden ${
              isProcessing
                ? 'bg-cyan-500/10 border-cyan-500/30'
                : 'bg-slate-700/50 border-slate-600/50'
            }`}>
              {isProcessing && (
                <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/20 to-transparent animate-pulse" />
              )}
              <span className="text-xs font-bold text-slate-400 uppercase">
                {file.name.split('.').pop()}
              </span>
            </div>

            {/* File Info */}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${isProcessing ? 'text-cyan-200' : 'text-slate-200'}`}>
                {file.name}
              </p>
              <p className="text-xs text-slate-500">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>

            {/* Processing Indicator or Delete Button */}
            {isProcessing ? (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-cyan-500/10 rounded-md">
                <Sparkles className="w-3 h-3 text-cyan-400 animate-pulse" />
                <span className="text-[10px] text-cyan-400 font-medium uppercase tracking-wider">
                  Queued
                </span>
              </div>
            ) : (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => onRemove(file.id)}
                className="text-slate-500 hover:text-red-400 hover:bg-red-500/10"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}